import React, { useMemo, useRef } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
// components
import { Grid } from '@mui/material';
import { DeleteButton } from 'components/delete-button/delete-button';
import StyledForm from 'components/styled-form';
import OutlinedInputController from 'controllers/outlined-input-controller';
import { SaveToLibraryCheckbox } from '../components/save-to-library-checkbox';
import SelectionOptions from '../components/selection-options';
// types
import useToast from 'hooks/useToast';
import { useParams } from 'react-router-dom';
import { useGetCompanyRolePermissionsMeQuery } from 'redux-store/api/company-roles';
import { ISelectionStepProps, TSelectionFormOption } from '../../utils/types';

type TOptions = {
  options: TSelectionFormOption[];
  [x: string]: unknown;
};
interface IProps<T extends FieldValues = TOptions>
  extends Omit<ISelectionStepProps, 'handleSubmit'> {
  isProjectSelection?: boolean;
  isEdit?: boolean;
  projectId?: number;
  isDescriptionEditDisable?: boolean;
  renderCostCodeAndLocation?: () => React.ReactElement;
  handleSubmit: (formValues: T) => void;
}

export const GeneralInfo = <T extends FieldValues = TOptions>({
  renderCostCodeAndLocation,
  renderActionButtons,
  handleSubmit,
  isProjectSelection = true,
  isEdit,
  projectId: projectIdProp,
  isSelectionApproved = false,
  isDescriptionEditDisable = false,
}: IProps<T>) => {
  const { projectId: projectIdFromParams } = useParams();
  const resolvedProjectId = projectIdProp || projectIdFromParams;
  const projectId = Number(resolvedProjectId);

  const parentRef = useRef<{ resetOptions: () => void }>(null);

  const { data: userPermissions } = useGetCompanyRolePermissionsMeQuery();

  const showToaster = useToast();

  const form = useFormContext<FieldValues>();

  const formOptions = form.watch('options');

  const showOptionName = useMemo(() => formOptions.length > 1, [formOptions.length]);

  const onResetOptions = () => {
    parentRef.current?.resetOptions();
  };

  const withInfoHandlingSubmit = (formValues: T) => {
    const uploadedFiles = formValues?.options?.flatMap(({ uploads }) => uploads);

    const hasPendingUploads = uploadedFiles?.some(
      (file) => !['done', 'error_file_size'].includes(file?.status)
    );

    if (hasPendingUploads) {
      showToaster({
        message: 'Uploads are still in progress. Please wait until they are fully uploaded.',
        type: 'info',
      });
      return;
    }

    handleSubmit(formValues);
  };

  return (
    <StyledForm onSubmit={form.handleSubmit(withInfoHandlingSubmit)}>
      {renderCostCodeAndLocation ? renderCostCodeAndLocation() : null}

      {showOptionName && (
        <OutlinedInputController
          fullWidth
          withError
          name="title"
          label="Option Name"
          placeholder=""
          disabled={isSelectionApproved}
        />
      )}

      <SelectionOptions
        projectId={projectId}
        isProjectSelection={isProjectSelection}
        isSelectionApproved={isSelectionApproved}
        isDescriptionEditDisable={isDescriptionEditDisable}
        parentRef={parentRef}
      />

      {isProjectSelection && !isEdit && userPermissions?.LIBRARY_SELECTIONS?.canCreate && (
        <SaveToLibraryCheckbox />
      )}

      <Grid sx={{ display: 'flex', alignItems: 'center' }}>
        <Grid sx={{ flex: 1 }}>
          {!isSelectionApproved && <DeleteButton text="Delete item" onClick={onResetOptions} />}
        </Grid>
        {renderActionButtons()}
      </Grid>
    </StyledForm>
  );
};
